import { useState, useEffect } from "react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { CalendarIcon, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { generateLohnausweis, downloadLohnausweis, type LohnausweisData } from "@/utils/lohnausweisGenerator";

interface Employee {
  id: string;
  name: string;
  firstname?: string;
  lastname?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  ahv_number?: string;
  birth_date?: string;
  street_address?: string;
}

interface LohnausweisExportDialogProps {
  preselectedEmployeeId?: string;
  preselectedEmployeeName?: string;
  children: React.ReactNode;
}

export function LohnausweisExportDialog({
  preselectedEmployeeId,
  preselectedEmployeeName,
  children,
}: LohnausweisExportDialogProps) {
  const [open, setOpen] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>(preselectedEmployeeId || "");
  const [startDate, setStartDate] = useState<Date | undefined>(new Date(new Date().getFullYear(), 0, 1));
  const [endDate, setEndDate] = useState<Date | undefined>(new Date(new Date().getFullYear(), 11, 31));
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open && !preselectedEmployeeId) {
      fetchEmployees();
    }
  }, [open]);

  useEffect(() => {
    if (preselectedEmployeeId) {
      setSelectedEmployeeId(preselectedEmployeeId);
    }
  }, [preselectedEmployeeId]);

  const fetchEmployees = async () => {
    const { data, error } = await supabase
      .from("employees")
      .select("id, name, firstname, lastname, address, city, postal_code, ahv_number, birth_date, street_address")
      .order("name");
    if (!error && data) {
      setEmployees(data);
    }
  };

  const handleExport = async () => {
    if (!selectedEmployeeId || !startDate || !endDate) {
      toast({ title: "Bitte alle Felder ausfuellen", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      // Fetch employee details
      const { data: emp, error: empError } = await supabase
        .from("employees")
        .select("id, name, firstname, lastname, address, city, postal_code, ahv_number, birth_date, street_address")
        .eq("id", selectedEmployeeId)
        .single();

      if (empError || !emp) throw new Error("Mitarbeiter nicht gefunden");

      if (!emp.ahv_number) {
        toast({
          title: "AHV-Nummer fehlt",
          description: "Bitte hinterlegen Sie die AHV-Nummer des Mitarbeiters vor dem Export.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Call RPC
      const { data: totals, error: rpcError } = await supabase.rpc("get_lohnausweis_data", {
        p_employee_id: selectedEmployeeId,
        p_start_date: format(startDate, "yyyy-MM-dd"),
        p_end_date: format(endDate, "yyyy-MM-dd"),
      });

      if (rpcError) throw rpcError;

      const row = Array.isArray(totals) ? totals[0] : totals;

      if (!row || row.period_count === 0) {
        toast({
          title: "Keine Lohndaten",
          description: "Fuer den gewaehlten Zeitraum wurden keine Lohnabrechnungen gefunden.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const lohnausweisData: LohnausweisData = {
        employee: {
          name: emp.name,
          firstname: emp.firstname || undefined,
          lastname: emp.lastname || undefined,
          address: emp.street_address || emp.address || undefined,
          city: emp.city || undefined,
          postal_code: emp.postal_code || undefined,
          ahv_number: emp.ahv_number || undefined,
          birth_date: emp.birth_date || undefined,
        },
        company: {
          name: "Walia Solutions AG",
          address: "Musterstrasse 1",
          city: "Zuerich",
          postal_code: "8000",
        },
        period: {
          start_date: format(startDate, "yyyy-MM-dd"),
          end_date: format(endDate, "yyyy-MM-dd"),
        },
        totals: {
          total_gross: Number(row.total_gross) || 0,
          total_net: Number(row.total_net) || 0,
          total_deductions: Number(row.total_deductions) || 0,
          total_hours: Number(row.total_hours) || 0,
          period_count: Number(row.period_count) || 0,
          ahv_total: Number(row.ahv_total) || 0,
          alv_total: Number(row.alv_total) || 0,
          bvg_total: Number(row.bvg_total) || 0,
          nbu_total: Number(row.nbu_total) || 0,
          ktg_total: Number(row.ktg_total) || 0,
          tax_total: Number(row.tax_total) || 0,
        },
      };

      const blob = generateLohnausweis(lohnausweisData);
      downloadLohnausweis(blob, emp.name, format(startDate, "yyyy-MM-dd"), format(endDate, "yyyy-MM-dd"));

      toast({ title: "Lohnausweis exportiert", description: `PDF fuer ${emp.name} wurde heruntergeladen.` });
      setOpen(false);
    } catch (error: any) {
      console.error("Lohnausweis export error:", error);
      toast({ title: "Fehler", description: error.message || "Export fehlgeschlagen", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Lohnausweis exportieren
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Employee selector */}
          {preselectedEmployeeId ? (
            <div>
              <label className="text-sm font-medium">Mitarbeiter</label>
              <p className="text-sm text-muted-foreground mt-1">{preselectedEmployeeName}</p>
            </div>
          ) : (
            <div>
              <label className="text-sm font-medium">Mitarbeiter</label>
              <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Mitarbeiter waehlen..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Start date */}
          <div>
            <label className="text-sm font-medium">Von</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn("w-full justify-start text-left font-normal mt-1", !startDate && "text-muted-foreground")}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, "dd.MM.yyyy", { locale: de }) : "Startdatum"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={setStartDate}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* End date */}
          <div>
            <label className="text-sm font-medium">Bis</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn("w-full justify-start text-left font-normal mt-1", !endDate && "text-muted-foreground")}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate ? format(endDate, "dd.MM.yyyy", { locale: de }) : "Enddatum"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={setEndDate}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleExport} disabled={loading || !selectedEmployeeId || !startDate || !endDate}>
            {loading ? "Wird exportiert..." : "Exportieren"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
